import { CONFIRMATION_SOURCE } from '@/constants/confirmationSource';
import FTLStopHelpers from '@/utilities/FTLStopHelpers';
import { isCustomerLeg } from '../map/assignToRouteHelpers';
import { isOrderAssigned } from '../planningDerivations';
import { isOrderCancelled } from './isOrderCancelled';

// Statuses the order card adds on top of the delivery_time_confirmed values.
// Both have entries in confirmationStatusColors in blocks.js.
export const NOT_ROUTED = 'not routed';
export const NOT_SENT = 'not sent';

// `unsure` has no color-map entry, so it maps onto the key that has one.
// Robocalls stopped writing it once the outcomes below were split out, but
// historical rows keep it and there's no backfill.
const UNSURE_STATUS = 'response unclear - call customer';

// Normalize before any comparison: UNRESOLVED_PRECEDENCE and
// confirmationStatusColors are both keyed on UNSURE_STATUS, so a check against
// the raw column value would silently miss an `unsure` row.
const normalizeStatus = (raw) => (raw === 'unsure' ? UNSURE_STATUS : raw);

// A manual confirm is the same confirmation state as an automated one — only the
// source differs. Keeping it out of delivery_time_confirmed is what lets the ~14
// `=== 'confirmed'` route-activation gates keep recognizing it.
const MANUAL_SOURCE = CONFIRMATION_SOURCE.MANUAL;

const LABELS = {
    confirmed: 'Confirmed',
    pending: 'Pending',
    rejected: 'Rejected',
    recalculate: 'Recalculate',
    'SMS failed': 'SMS Failed',
    voicemail: 'Voicemail',
    'answered no response': 'Answered No Response',
    'no response': 'No Response',
    failed: 'Failed',
    [UNSURE_STATUS]: 'Response Unclear',
    [NOT_ROUTED]: 'Not Routed',
    [NOT_SENT]: 'Not Sent',
};

/**
 * Maps an order row to the confirmation pill's status key and display text.
 * A missing status means two different things — the order isn't on a route
 * yet, or it is but no confirmation has gone out — so they get distinct pills.
 *
 * @param {object} order planning order row (needs the injected crossdock_leg / *_route_id)
 * @returns {{ status: string, label: string }}
 */
export const getOrderConfirmation = (order) => {
    // Read the status only on the leg that owns it. Both columns are order-level
    // scalars belonging to the customer-facing leg, but deriveOrderRows spreads
    // one source order into both crossdock rows — so the warehouse leg would
    // otherwise pill its sibling's outcome for a call it never made.
    const raw = isCustomerLeg(order) ? order?.delivery_time_confirmed : null;

    if (!raw) {
        // isOrderAssigned only guards its pickup branch, so don't hand it a missing row.
        const status = order && isOrderAssigned(order) ? NOT_SENT : NOT_ROUTED;
        return { status, label: LABELS[status] };
    }

    const status = normalizeStatus(raw);

    // Relabel only — the status key stays `confirmed` so the pill keeps its color.
    if (status === 'confirmed' && order?.confirmation_source === MANUAL_SOURCE) {
        return { status, label: 'Manually Confirmed' };
    }

    // Unknown future values fall through uncolored rather than throwing.
    return { status, label: LABELS[status] || status };
};

// Outcomes that outrank the `pending` fallback when a stop mixes statuses.
// `recalculate` and `rejected` keep their own earlier checks below.
export const UNRESOLVED_PRECEDENCE = [
    'SMS failed',
    'failed',
    'voicemail',
    'answered no response',
    'no response',
    UNSURE_STATUS,
];

/** Display text for a status key, for callers that already have one. */
export const getConfirmationLabel = (status) => LABELS[status] || status;

/** We reached the customer's line and got no confirmation — dispatch has to follow up. */
export const isUnresolvedConfirmation = (status) => UNRESOLVED_PRECEDENCE.includes(status);

/**
 * Whether a set of orders got to `confirmed` by hand rather than from the customer.
 * Requires every confirmed order to be manual — a stop where one order came back
 * from a robocall did get a real customer confirmation, so it reads as Confirmed.
 * Cancelled orders are ignored; they carry no meaningful source.
 *
 * @param {object[]} orders order rows to judge together
 * @returns {boolean}
 */
export const isManualConfirmation = (orders = []) => {
    const confirmed = orders.filter(
        (order) => order && !isOrderCancelled(order) && order.delivery_time_confirmed === 'confirmed'
    );
    return confirmed.length > 0 && confirmed.every((order) => order.confirmation_source === MANUAL_SOURCE);
};

/**
 * Rolls a stop's orders up to one badge status. Cancelled orders only contribute
 * when nothing else has; any unresolved call outcome beats the pending fallback,
 * so a Voicemail order doesn't read as Pending next to a pending sibling. A blank
 * status counts as pending once any sibling carries one.
 *
 * @param {object[]} stopOrders order rows on the stop
 * @returns {string|null} status key, or null when no order carries one
 */
export const getStopConfirmationStatus = (stopOrders = []) => {
    // A stop nobody has been contacted about shows no badge at all, so answer
    // that here rather than letting the blank-to-pending default below speak for it.
    if (!stopOrders.some((order) => order?.delivery_time_confirmed)) return null;

    return stopOrders.reduce((status, order) => {
        const raw = normalizeStatus(order?.delivery_time_confirmed);

        if (isOrderCancelled(order)) {
            if (status) return status;
            return raw;
        }

        // Same blank-to-pending default getRouteConfirmationStatus applies before
        // calling this reducer. Without it a blank first order leaves the
        // accumulator falsy, the empty-accumulator branch below fires a second
        // time, and it returns the next order's status outright — so a
        // never-contacted order could sit on a stop badged Confirmed.
        const value = raw || 'pending';

        if (status === 'recalculate' || value === 'recalculate') {
            return 'recalculate';
        }

        // Only a leading run of cancelled orders can still leave this empty.
        if (!status) return value;

        // Rejected if any order is rejected
        if (status === 'rejected' || value === 'rejected') return 'rejected';

        const unresolved = UNRESOLVED_PRECEDENCE.find((s) => status === s || value === s);
        if (unresolved) return unresolved;

        // Confirmed if all orders are confirmed
        if (status === 'confirmed' && value === 'confirmed') return 'confirmed';

        // Pending for any other combination of pending/confirmed
        return 'pending';
    }, null);
};

/**
 * Stop roll-up plus its display text, so the stop row can say "Manually
 * Confirmed" the same way the order card does — the status key alone can't carry
 * that, since manual confirms deliberately stay on `confirmed`.
 *
 * @param {object[]} stopOrders order rows on the stop
 * @returns {{ status: string|null, label: string|null }}
 */
export const getStopConfirmation = (stopOrders = []) => {
    const status = getStopConfirmationStatus(stopOrders);

    if (!status) return { status: null, label: null };
    if (status === 'confirmed' && isManualConfirmation(stopOrders)) {
        return { status, label: 'Manually Confirmed' };
    }

    return { status, label: getConfirmationLabel(status) };
};

/**
 * Rolls a whole route up to the one status its summary chip shows, reusing the
 * stop reducer so the route tab and the stop rows can't drift apart.
 *
 * Cancelled orders are dropped before the stop roll-up rather than being allowed
 * to contribute — a cancelled order must never hold a route back, which is the
 * same rule routeIsConfirmed applies in util/index.js. `recalculate` is left to
 * collapse into `pending`: the route card signals that through need_to_optimize.
 *
 * @param {object} route route row with stopsByRouteId
 * @param {object} ordersByKey order rows keyed by order_id
 * @returns {string|null} status key, or null for a route with no stops
 */
export const getRouteConfirmationStatus = (route, ordersByKey = {}) =>
    (route?.stopsByRouteId || []).reduce((status, stop) => {
        if (!FTLStopHelpers.isCustomerStop(stop, route)) {
            if (status) return status;
            return stop.del_window_start ? 'confirmed' : 'pending';
        }

        const stopOrders = (stop.orders || [])
            .map((order_id) => ordersByKey[order_id])
            .filter((order) => order && !isOrderCancelled(order))
            // A blank status counts as pending here so the route chip can't read
            // "confirmed" just because a sibling order was never contacted. The
            // stop rows show that order as its own Not Called pill instead.
            .map((order) => (order.delivery_time_confirmed ? order : { ...order, delivery_time_confirmed: 'pending' }));

        const stopStatus = getStopConfirmationStatus(stopOrders);

        if (!status) {
            // Collapse recalculate to pending, matching the trailing branches' behavior.
            // A mixed [recalculate, confirmed] stop shouldn't propagate recalculate as
            // the route's confirmationStatus — need_to_optimize signals the route edit.
            if (stopStatus === 'recalculate') return 'pending';
            return stopStatus || 'pending';
        }
        if (status === 'rejected' || stopStatus === 'rejected') return 'rejected';

        const unresolved = UNRESOLVED_PRECEDENCE.find((s) => status === s || stopStatus === s);
        if (unresolved) return unresolved;

        if (status === 'confirmed' && stopStatus === 'confirmed') return 'confirmed';
        return 'pending';
    }, null);
